import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  GitCompareArrows,
  Loader2,
  RefreshCcw,
  ShoppingBag,
  Sparkles,
  Store,
} from "lucide-react";
import PageContainer from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/lib/localization";
import { useAuth } from "@/lib/store";
import { fetchAnalyticsPatternsPage } from "@/lib/shopApi";

const PAGE_LIMIT = 40;

function normalizePatterns(response) {
  return response?.buyingPatterns ?? null;
}

function formatGeneratedAt(value, locale, copy) {
  if (!value) return copy.reviewedNow;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return copy.reviewedNow;
  return copy.reviewedAt(new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date));
}

function formatNumber(value, locale) {
  if (value === null || value === undefined) return "0";
  return Number(value).toLocaleString(locale, {
    maximumFractionDigits: 2,
  });
}

export default function AnalyticsBuyingPatternsList() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { locale, messages } = useLocalization();
  const copy = messages.analytics.buyingPatterns;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  const loadPatterns = useCallback(async (nextOffset = 0) => {
    if (!session?.sessionId) {
      setError(messages.analytics.sessionNotReady);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetchAnalyticsPatternsPage(session.sessionId, {
        limit: PAGE_LIMIT,
        offset: nextOffset,
      });
      setData(normalizePatterns(response));
      setOffset(nextOffset);
    } catch (err) {
      setError(err?.message || copy.errorTitle);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [copy.errorTitle, messages.analytics.sessionNotReady, session?.sessionId]);

  useEffect(() => {
    void loadPatterns(0);
  }, [loadPatterns]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const patterns = useMemo(() => data?.patterns ?? [], [data?.patterns]);
  const strongestPair = patterns[0];
  const totalCount = data?.totalCount ?? 0;
  const currentPageNumber = totalCount ? Math.floor((data?.offset ?? 0) / PAGE_LIMIT) + 1 : 1;
  const totalPages = totalCount ? Math.max(1, Math.ceil(totalCount / PAGE_LIMIT)) : 1;

  return (
    <PageContainer className="px-4 py-6 sm:px-6 lg:px-8">
      <section className="mx-auto w-full max-w-3xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 mb-3 h-8 px-2 text-muted-foreground hover:text-foreground"
              onClick={() => navigate("/analytics/customer-buying-patterns")}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {copy.title}
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">{copy.listTitle}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {copy.listSubtitle}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            disabled={loading}
            onClick={() => void loadPatterns(offset)}
          >
            <RefreshCcw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {messages.analytics.refresh}
          </Button>
        </div>

        {loading ? (
          <div className="luxe-card rounded-lg p-6">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              {copy.listLoading}
            </div>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="luxe-card rounded-lg border-destructive/25 p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight">{copy.listErrorTitle}</h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{error}</p>
                <Button className="mt-4 h-9" onClick={() => void loadPatterns(offset)}>
                  {messages.analytics.tryAgain}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {!loading && !error && patterns.length > 0 ? (
          <div className="space-y-3">
            {patterns.map((pattern) => (
              <PatternCard
                key={`${pattern.firstProduct}-${pattern.secondProduct}`}
                pattern={pattern}
              />
            ))}

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/70 bg-background/70 px-4 py-3">
              <div className="text-sm text-muted-foreground">
                {messages.analytics.pageSummary(currentPageNumber, totalPages, totalCount)}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  disabled={loading || offset === 0}
                  onClick={() => void loadPatterns(Math.max(0, offset - PAGE_LIMIT))}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  {messages.analytics.previous}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  disabled={loading || !data?.hasMore}
                  onClick={() => void loadPatterns(data?.nextOffset ?? offset)}
                >
                  {messages.analytics.next}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {!loading && !error && patterns.length === 0 ? (
          <div className="luxe-card rounded-lg p-5 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/15">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="inline-flex items-center rounded-full border border-border/70 bg-background/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                    {copy.summaryBuilding}
                  </div>
                  <h2 className="mt-2 text-xl font-semibold tracking-tight">{copy.noReliablePairing}</h2>
                  <p className="mt-1 max-w-xl text-sm leading-5 text-muted-foreground">
                    {copy.noReliablePairingDescription}
                  </p>
                </div>
              </div>
              <div className="pt-1 text-sm text-muted-foreground sm:text-right">
                {formatGeneratedAt(data?.generatedAt, locale, copy)}
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <Metric
                label={copy.salesChecked}
                value={formatNumber(data?.transactionsCount, locale)}
              />
              <Metric
                label={copy.salesWindow}
                value={`${data?.salesWindowDays ?? 0} day${data?.salesWindowDays === 1 ? "" : "s"}`}
              />
              <Metric
                label={copy.strongestLift}
                value={strongestPair ? formatNumber(strongestPair.lift, locale) : "0"}
              />
            </div>
          </div>
        ) : null}
      </section>
    </PageContainer>
  );
}

function PatternCard({ pattern }) {
  const { locale, messages } = useLocalization();
  const copy = messages.analytics.buyingPatterns;
  return (
    <article className="luxe-card rounded-lg p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <GitCompareArrows className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold tracking-tight">
              {pattern.firstProduct} + {pattern.secondProduct}
            </h3>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              {pattern.recommendation}
            </p>
          </div>
        </div>
        <span className="inline-flex items-center rounded-md border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
          {copy.paymentSaleCount(pattern.pairCount)}
        </span>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        <Metric label={copy.shareOfSales} value={`${formatNumber(pattern.supportPercent, locale)}%`} strong />
        <Metric label={copy.pairStrength} value={`${formatNumber(pattern.confidencePercent, locale)}%`} strong />
        <Metric label={copy.extraSignal} value={formatNumber(pattern.lift, locale)} />
        <Metric label={copy.pairFrequency} value={copy.paymentSaleCount(pattern.pairCount ?? 0)} />
        <Metric label={copy.firstProduct} value={pattern.firstProduct} icon />
        <Metric label={copy.secondProduct} value={pattern.secondProduct} icon />
      </div>
    </article>
  );
}

function Metric({ label, value, strong = false, icon = false }) {
  return (
    <div className="rounded-md border border-border/70 bg-background/60 px-3 py-3">
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label === "Sales checked" ? <Store className="h-3.5 w-3.5" /> : null}
        {label === "Sales window" ? <ShoppingBag className="h-3.5 w-3.5" /> : null}
        {label === "Strongest lift" ? <Sparkles className="h-3.5 w-3.5" /> : null}
        {icon && label === "First product" ? <GitCompareArrows className="h-3.5 w-3.5" /> : null}
        {icon && label === "Second product" ? <GitCompareArrows className="h-3.5 w-3.5" /> : null}
        {label}
      </div>
      <div className={`mt-1 ${icon ? "truncate font-sans" : "font-mono"} text-sm tabular-nums ${strong ? "font-semibold text-foreground" : "font-medium text-foreground/85"}`}>
        {value}
      </div>
    </div>
  );
}
